<script setup lang="ts">
import { ref } from 'vue'

const heroSection = ref<HTMLElement>()
</script>

<template>
  <div
    id="home"
    class="hero-wrapper"
  >
    <div id="landingHero">
      <div
        ref="heroSection"
        class="landing-hero"
      >
        <VContainer class="hero-container">
          <VRow
            align="center"
            class="hero-row"
          >
            <!-- Left Content -->
            <VCol
              cols="12"
              md="6"
              class="hero-content"
            >
              <div class="hero-logo-wrapper mb-8 animate-fade-in-up">
                <Lottie
                  link="/animations/tspeelfabrik.json"
                  :height="300"
                  :width="600"
                  loop
                  :speed="1.5"
                />
              </div>
              <h3 class="hero-tagline animate-fade-in-up animate-delay-1">
                Avonturen die je zelf mee bouwt!
              </h3>
            </VCol>

            <!-- Right Image -->
            <VCol
              cols="12"
              md="6"
              class="hero-image-container"
            >
              <div class="hero-image-wrapper animate-fade-in-scale animate-delay-2">
                <NuxtImg
                  src="/images/hero/Heroafbeelding2.png"
                  alt="Happy kids at camp"
                  class="hero-image"
                  width="640"
                  height="606"
                  fetchpriority="high"
                  loading="eager"
                  format="webp"
                  placeholder
                />
              </div>
            </VCol>
          </VRow>
        </VContainer>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.landing-hero {
  position: relative;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  min-block-size: 500px;
  padding-block: 4rem 0;
}

.hero-container {
  position: relative;
  z-index: 10;
}

.hero-row {
  min-block-size: 400px;
}

.hero-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-block: 2rem;
  padding-inline-end: 2rem;
}

.hero-title {
  color: rgb(var(--v-theme-on-surface));
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 4rem;
  font-weight: 700;
  line-height: 1.1;
  margin-block-end: 1rem;
}

.hero-subtitle {
  color: #e87b35;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1.75rem;
  font-weight: 400;
  line-height: 1.4;
}

.hero-logo-wrapper {
  display: flex;
  justify-content: center;
  inline-size: 100%;
}

.hero-logo {
  block-size: auto;
  inline-size: 100%;
  max-inline-size: 400px;
}

.hero-tagline {
  color: #e87b35;
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1.75rem;
  font-weight: 400;
  line-height: 1.4;
  text-align: center;
}

.hero-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.hero-btn {
  font-family: "Peachy Kee JF", "Public Sans", sans-serif;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding-block: 0.75rem;
  padding-inline: 2rem;
  text-transform: uppercase;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgb(0 0 0 / 15%);
    transform: translateY(-2px);
  }
}

.hero-btn-primary {
  color: #fff;
}

.hero-btn-secondary {
  color: #fff;
}

.hero-image-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-block: 2rem;
}

.hero-image-wrapper {
  position: relative;
  inline-size: 100%;
  max-inline-size: 500px;
}

.hero-image {
  border-radius: 50%;
  block-size: auto;
  inline-size: 100%;
  object-fit: cover;
}

/* Animations */
.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out forwards;
  opacity: 0;
}

.animate-fade-in-scale {
  animation: fade-in-scale 1s ease-out forwards;
  opacity: 0;
}

.animate-delay-1 {
  animation-delay: 0.2s;
}

.animate-delay-2 {
  animation-delay: 0.4s;
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-scale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Responsive */
@media (max-width: 960px) {
  .hero-title {
    font-size: 3rem;
  }

  .hero-subtitle {
    font-size: 1.5rem;
  }

  .hero-content {
    padding-inline-end: 0;
    text-align: center;
  }

  .hero-logo {
    max-inline-size: 300px;
  }

  .hero-buttons {
    justify-content: center;
  }

  .hero-image-wrapper {
    max-inline-size: 400px;
  }
}

@media (max-width: 599px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.25rem;
  }

  .hero-row {
    min-block-size: auto;
  }

  .hero-logo {
    max-inline-size: 250px;
  }

  .hero-buttons {
    flex-direction: column;

    .hero-btn {
      inline-size: 100%;
    }
  }

  .hero-image-container {
    display: none;
  }
}
</style>
